import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate } from '../api/dataverseClient';

export interface TenantConfig {
  dagrun_pm_tenantconfigid: string;
  dagrun_pm_name: string;
  dagrun_pm_rfpenabled: boolean | null;
  dagrun_pm_bdleadsjson?: string | null;
  dagrun_pm_beaconenvurl?: string | null;
}

export interface BDLead { oid: string; name: string }

const FIELDS = ['dagrun_pm_tenantconfigid', 'dagrun_pm_name', 'dagrun_pm_rfpenabled', 'dagrun_pm_bdleadsjson', 'dagrun_pm_beaconenvurl'];

/**
 * Returns the singleton tenant config row, creating it on first call.
 * The table contains exactly one row per tenant (this app is tenanted
 * via the Dataverse environment, not multi-tenant within an env), so
 * we treat it as a key-value record. Calling code reads/writes the
 * single row and the GUID is stable across sessions.
 *
 * The list query happens before the create attempt so we don't write
 * a duplicate row when one already exists.
 */
export async function getOrCreateTenantConfig(pca: IPublicClientApplication): Promise<TenantConfig> {
  const rows = await dvList<TenantConfig>(pca, 'dagrun_pm_tenantconfigs', {
    select: FIELDS,
    top: 1,
  });
  if (rows.length > 0) return rows[0];
  const newId = await dvCreate(pca, 'dagrun_pm_tenantconfigs', {
    dagrun_pm_name: 'Tenant Config',
    dagrun_pm_rfpenabled: false,
  });
  return {
    dagrun_pm_tenantconfigid: newId,
    dagrun_pm_name: 'Tenant Config',
    dagrun_pm_rfpenabled: false,
  };
}

/**
 * Cached version for read-only checks (Projects toolbar gating, etc.).
 * Cache lasts for the session — admin toggles invalidate via
 * setTenantRFPEnabled, which writes through and updates the cache.
 */
let cached: TenantConfig | null = null;
export async function getTenantConfigCached(pca: IPublicClientApplication): Promise<TenantConfig> {
  if (cached) return cached;
  cached = await getOrCreateTenantConfig(pca);
  return cached;
}

export async function setTenantRFPEnabled(
  pca: IPublicClientApplication,
  enabled: boolean,
): Promise<void> {
  const cfg = await getOrCreateTenantConfig(pca);
  await dvUpdate(pca, 'dagrun_pm_tenantconfigs', cfg.dagrun_pm_tenantconfigid, {
    dagrun_pm_rfpenabled: enabled,
  });
  cached = { ...cfg, dagrun_pm_rfpenabled: enabled };
}

// ── BD Lead roster (RFP pipeline) ──────────────────────────────────────
// Stored as a JSON array on the singleton tenant config row.

export function parseBDLeads(cfg: TenantConfig): BDLead[] {
  if (!cfg.dagrun_pm_bdleadsjson) return [];
  try {
    const arr = JSON.parse(cfg.dagrun_pm_bdleadsjson);
    return Array.isArray(arr) ? arr.filter((x): x is BDLead => !!x && typeof x.oid === 'string' && typeof x.name === 'string') : [];
  } catch { return []; }
}

export async function getBDLeads(pca: IPublicClientApplication): Promise<BDLead[]> {
  return parseBDLeads(await getOrCreateTenantConfig(pca));
}

export async function setBDLeads(pca: IPublicClientApplication, leads: BDLead[]): Promise<void> {
  const cfg = await getOrCreateTenantConfig(pca);
  const json = JSON.stringify(leads);
  await dvUpdate(pca, 'dagrun_pm_tenantconfigs', cfg.dagrun_pm_tenantconfigid, {
    dagrun_pm_bdleadsjson: json,
  });
  cached = { ...cfg, dagrun_pm_bdleadsjson: json };
}

// ── Beacon (Dagrun CRM) environment URL cache ──────────────────────────
// Beacon lives in a SEPARATE Dataverse environment. Its base URL is a stable,
// tenant-wide fact resolved once via Global Discovery (see beaconClient) and
// cached here so every user/session skips re-discovery. Empty string = "resolved,
// not present"; null/absent = not yet resolved.

export async function getBeaconEnvUrl(pca: IPublicClientApplication): Promise<string | null> {
  const cfg = await getOrCreateTenantConfig(pca);
  const v = cfg.dagrun_pm_beaconenvurl;
  return v && v.trim() ? v.trim() : null;
}

export async function setBeaconEnvUrl(pca: IPublicClientApplication, url: string): Promise<void> {
  const cfg = await getOrCreateTenantConfig(pca);
  await dvUpdate(pca, 'dagrun_pm_tenantconfigs', cfg.dagrun_pm_tenantconfigid, {
    dagrun_pm_beaconenvurl: url,
  });
  cached = { ...cfg, dagrun_pm_beaconenvurl: url };
}
